import {
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@material-ui/core";
import {
  ContactSupport,
  EventNote,
  Message,
  LibraryBooks,
  Domain,
  InsertChart,
  Face,
  SmsFailed,
  School,
  GridOn,
  MenuBook,
} from "@material-ui/icons";
import React from "react";
import { useHistory } from "react-router-dom";
import { Activity } from "../domain/Activity";
import { UserRole } from "../domain/userRole";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { authSelector } from "../redux/selectors";

export type HistoryListItemProps = {
  item: Activity;
};
export const HistoryListItem: React.FC<HistoryListItemProps> = ({ item }) => {
  const history = useHistory();

  const { isInRole } = useAuth(authSelector);
  const { type, path, icon } = getActivityDetails(item, isInRole);
  const navigate = () => history.push(path || "");

  return (
    <ListItem
      button
      alignItems="flex-start"
      onClick={() => navigate()}
      divider
      data-test="recently-viewed-item"
    >
      <ListItemAvatar>
        <Avatar>{icon}</Avatar>
      </ListItemAvatar>

      {/* cant make it do the ellipsis but at least it doesn't overflow */}
      <ListItemText
        style={{ overflow: "hidden", textOverflow: "ellipsis" }}
        primary={item.activityTitle}
        secondary={type}
      ></ListItemText>
    </ListItem>
  );
};

const getActivityDetails = (
  item: Activity,
  isInRole: (...roles: UserRole[]) => boolean
) => {
  // resource type is the only identifier we have to tell the different types at this point
  // should we introduce an int column to the table or something in activity meta?
  const body: { urlResourceIdentifier: string } =
    (!!item.activityMetaData && item.activityMetaData !== "") && JSON.parse(item.activityMetaData);

  const isUrlIdentifier = body && !!body.urlResourceIdentifier;

  switch (item.resourceType) {
    case "contact-ao":
      return {
        type: "Contact awarding organisation",
        path: `/communications/contact-ao/${item.resourceId}`,
        icon: <Message />,
      };
    case "event-notifications":
      return {
        type: "Event notification",
        path: `/communications/event-notifications/${item.resourceId}`,
        icon: <EventNote />,
      };
    case "contact-the-regulator":
      return {
        type: "Contact the regulator",
        path: `/communications/contact-the-regulator/${item.resourceId}`,
        icon: <ContactSupport />,
      };
    case "news":
      return {
        type: "News",
        path: isInRole(UserRole.ITAdmin, UserRole.OfqualNewsAdmin)
          ? `/news/${item.resourceId}`
          : `/articles/${item.resourceId}`,
        icon: <LibraryBooks />,
      };
    case "Organisation":
      return {
        type: "Organisation",
        path: isUrlIdentifier && `/organisations/${body.urlResourceIdentifier}`,
        icon: <Domain />,
      };
    case "scope":
      return {
        type: "Organisation scope application",
        path:
          isUrlIdentifier &&
          `/organisations/${body.urlResourceIdentifier}/scope/applications/${item.resourceId}`,
        icon: <Domain />,
      };
    case "recognition":
      return {
        type: "Organisation recognition",
        path:
          isUrlIdentifier &&
          `/organisations/${body.urlResourceIdentifier}/recognition`,
        icon: <Domain />,
      };
    case "svr":
      return {
        type: "SVR",
        path: `/svr/${item.resourceId}`,
        icon: <InsertChart />,
      };
    case "users":
      return {
        type: "Users",
        path: isUrlIdentifier && `/users/${body.urlResourceIdentifier}`,
        icon: <Face />,
      };
    case "complaints":
      return {
        type: "Complaint",
        path: `/complaints/${item.resourceId}`,
        icon: <SmsFailed />,
      };
    case "qualifications":
      return {
        type: "Qualification",
        path: `/qualifications/${item.resourceId}`,
        icon: <School />,
      };
    case "units":
      return {
        type: "Units",
        path: `/units/${item.resourceId}`,
        icon: <GridOn />,
      };
    case "APQ":
      return {
        type: "APQs",
        path: `/assessment-plan-query/${item.resourceId}`,
        icon: <MenuBook />,
      };
    case "soc":
      return {
        type: "SoC",
        path: `/soc/${item.resourceId}`,
        icon: <Domain />,
      };
    default:
      console.warn(
        `Missing renderer and path for activity with resource type ${item.resourceType} and resourceId ${item.resourceId}`
      );
      return { path: null, icon: null };
  }
};
